"use client";

import {
	Button,
	Heading,
	Icon,
	Image,
	Price,
	RichText,
	Tag
} from "@pamperedchef/design-system";
import type { Cta } from "@/composites/landing/types";
import { CheckList } from "@/composites/landing/CheckList";
import * as styles from "./OfferCard.module.scss";

/**
 * Best-effort parse of fixture-style price strings (e.g. `"$0.00"`, `"$299.00"`)
 * into a numeric value for the DS `Price` component. Returns `undefined` when
 * the string can't be coerced — caller renders nothing in that case.
 */
function parsePriceText(text: string | undefined): number | undefined {
	if (!text) return undefined;
	const cleaned = text.replace(/[^0-9.\-]/g, "");
	if (!cleaned) return undefined;
	const value = Number.parseFloat(cleaned);
	return Number.isFinite(value) ? value : undefined;
}

export interface OfferCardProps {
	badgeText?: string;
	title: string;
	priceText?: string;
	originalPriceText?: string;
	description?: string;
	features?: string[];
	disclaimer?: { title?: string; body?: string };
	cta?: Cta;
	image?: { url: string; alt?: string };
}

/**
 * Featured exclusive-offer card — the VIP free-kit hero offer. Cream card,
 * two-column on desktop (image left, content right): a mint "Free Kit" `Tag`,
 * a teal serif title, a price row (bold current + struck-through original),
 * description, divider, a feature `CheckList`, a white info note, and a yellow
 * filled CTA. Presentational, props-in, RSC-safe.
 */
export function OfferCard({
	badgeText,
	title,
	priceText,
	originalPriceText,
	description,
	features,
	disclaimer,
	cta,
	image
}: OfferCardProps) {
	return (
		<section className={styles["offer-card"]} data-component="OfferCard">
			{image ? (
				<div className={styles["offer-card__media"]}>
					<Image
						src={image.url}
						alt={image.alt ?? title}
						className={styles["offer-card__image"]}
						objectFit="cover"
					/>
				</div>
			) : null}

			<div className={styles["offer-card__body"]}>
				{badgeText ? (
					<Tag
						kind="subtle"
						color="success"
						size="sm"
						label={badgeText}
						className={styles["offer-card__badge"]}
					/>
				) : null}

				<Heading
					tag="h3"
					styledAs="h3"
					className={styles["offer-card__title"]}
				>
					{title}
				</Heading>

				{priceText ? (
					// TODO STORE-007: swap to <PriceDisplay /> from @/composites/product/PriceDisplay
					// once that stub is implemented (currently returns a placeholder Stub).
					<Price
						className={styles["offer-card__price"]}
						display={parsePriceText(priceText)}
						strikeThrough={parsePriceText(originalPriceText)}
						size="large"
					/>
				) : null}

				{description ? (
					<RichText
						content={description}
						className={styles["offer-card__description"]}
					/>
				) : null}

				{features && features.length > 0 ? (
					<>
						<hr className={styles["offer-card__divider"]} />
						<CheckList items={features} />
					</>
				) : null}

				{disclaimer && (disclaimer.title || disclaimer.body) ? (
					<aside className={styles["offer-card__disclaimer"]}>
						<span
							className={styles["offer-card__disclaimer-icon"]}
							aria-hidden="true"
						>
							<Icon name="info" size={20} />
						</span>
						<span className={styles["offer-card__disclaimer-text"]}>
							{disclaimer.title ? (
								<span
									className={
										styles["offer-card__disclaimer-title"]
									}
								>
									{disclaimer.title}
								</span>
							) : null}
							{disclaimer.body ? (
								<span
									className={
										styles["offer-card__disclaimer-body"]
									}
								>
									{disclaimer.body}
								</span>
							) : null}
						</span>
					</aside>
				) : null}

				{cta ? (
					<Button
						kind="primary"
						size="lg"
						onPress={() => {
							window.location.href = cta.href;
						}}
						className={styles["offer-card__cta"]}
					>
						{cta.title}
					</Button>
				) : null}
			</div>
		</section>
	);
}
